
import { MLCourse } from './types';

export const ML_FUNDAMENTALS_COURSE: MLCourse = {
  id: 'ml-fundamentals',
  title: 'Machine Learning Fundamentals',
  description: 'Master the core concepts of machine learning, from data preprocessing to model evaluation. Perfect for beginners with programming background.',
  level: 'fundamentals',
  totalDuration: '12-15 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Basic programming knowledge (Python preferred)',
    'High school mathematics',
    'Basic statistics understanding'
  ],
  learningOutcomes: [
    'Understand core ML concepts and terminology',
    'Preprocess and clean data for ML models',
    'Build and evaluate supervised learning models',
    'Apply unsupervised learning techniques',
    'Use Python libraries like Pandas, NumPy, and Scikit-learn',
    'Implement complete ML projects from start to finish',
    'Understand ML ethics and bias considerations'
  ],
  modules: [
    {
      id: 'ml-intro-theory',
      title: 'Machine Learning Introduction & Theory',
      description: 'Understanding what machine learning is, different types, real-world applications, and ethical considerations.',
      duration: '90 minutes',
      difficulty: 'beginner',
      objectives: [
        'Define machine learning and its core concepts',
        'Distinguish between supervised, unsupervised, and reinforcement learning',
        'Identify real-world ML applications',
        'Understand ethical implications of ML systems'
      ],
      content: `# Machine Learning Introduction & Theory

## What is Machine Learning?

Machine Learning (ML) is a subset of artificial intelligence that enables computers to learn and make decisions from data without being explicitly programmed for every task.

### Key Concepts:
- **Algorithm**: A set of rules or instructions for solving a problem
- **Model**: The output of an algorithm after training on data
- **Training**: The process of teaching an algorithm using historical data
- **Prediction**: Using a trained model to make decisions on new data

## Types of Machine Learning

### 1. Supervised Learning
- **Definition**: Learning with labeled examples
- **Goal**: Map inputs to correct outputs
- **Examples**: Email spam detection, price prediction, medical diagnosis

### 2. Unsupervised Learning
- **Definition**: Finding patterns in data without labels
- **Goal**: Discover hidden structures
- **Examples**: Customer segmentation, anomaly detection, data compression

### 3. Reinforcement Learning
- **Definition**: Learning through trial and error with rewards
- **Goal**: Maximize cumulative reward
- **Examples**: Game playing, robotics, recommendation systems

## Real-World Applications

### Healthcare
- Medical image analysis
- Drug discovery
- Personalized treatment plans

### Finance
- Fraud detection
- Algorithmic trading
- Credit scoring

### Technology
- Search engines
- Recommendation systems
- Voice assistants

## Ethics in Machine Learning

### Key Considerations:
1. **Bias and Fairness**: Ensuring models don't discriminate
2. **Privacy**: Protecting user data
3. **Transparency**: Making decisions explainable
4. **Accountability**: Taking responsibility for ML outcomes

### Best Practices:
- Diverse training data
- Regular bias audits
- Clear documentation
- Human oversight`,
      codeExamples: [
        {
          title: 'Simple ML Example - Linear Relationship',
          language: 'python',
          code: `import numpy as np
import matplotlib.pyplot as plt

# Generate sample data
hours_studied = np.array([1, 2, 3, 4, 5, 6, 7, 8])
test_scores = np.array([50, 55, 65, 70, 75, 80, 85, 90])

# Simple linear relationship
plt.scatter(hours_studied, test_scores)
plt.xlabel('Hours Studied')
plt.ylabel('Test Score')
plt.title('Relationship between Study Time and Test Scores')
plt.show()

# This demonstrates supervised learning - we have inputs (hours) and outputs (scores)`,
          explanation: 'This example shows the fundamental concept of supervised learning where we have input features (hours studied) and target outputs (test scores).'
        }
      ],
      quiz: {
        questions: [
          {
            id: 'q1',
            question: 'Which type of machine learning uses labeled data?',
            options: ['Supervised Learning', 'Unsupervised Learning', 'Reinforcement Learning', 'Deep Learning'],
            correctAnswer: 0,
            explanation: 'Supervised learning uses labeled examples to learn the mapping between inputs and outputs.'
          },
          {
            id: 'q2',
            question: 'What is a key ethical consideration in ML?',
            options: ['Model accuracy', 'Processing speed', 'Bias and fairness', 'Data storage'],
            correctAnswer: 2,
            explanation: 'Bias and fairness are crucial ethical considerations to ensure ML systems treat all groups fairly.'
          }
        ],
        passThreshold: 75
      }
    },
    {
      id: 'ml-data-preprocessing',
      title: 'Data Preprocessing & Feature Engineering',
      description: 'Learn to clean, transform, and prepare data for machine learning models.',
      duration: '2 hours',
      difficulty: 'beginner',
      objectives: [
        'Handle missing data effectively',
        'Encode categorical variables',
        'Scale and normalize features',
        'Create new features from existing data'
      ],
      content: `# Data Preprocessing & Feature Engineering

Data preprocessing is often 80% of the machine learning workflow. Clean, well-prepared data is crucial for model success.

## Handling Missing Data

### Strategies:
1. **Deletion**: Remove rows or columns with missing values
2. **Imputation**: Fill missing values with estimates
3. **Interpolation**: Use patterns to estimate missing values

### Common Imputation Methods:
- **Mean/Median**: For numerical data
- **Mode**: For categorical data
- **Forward/Backward Fill**: For time series
- **KNN Imputation**: Use similar samples

## Categorical Data Encoding

### One-Hot Encoding
Convert categories into binary columns:
- Color: Red, Blue, Green
- Becomes: Color_Red (0/1), Color_Blue (0/1), Color_Green (0/1)

### Label Encoding
Convert categories to numbers:
- Size: Small=1, Medium=2, Large=3

## Feature Scaling

### Why Scale?
- Different units (age vs income)
- Different ranges (0-1 vs 0-100000)
- Algorithm performance

### Methods:
- **Standardization**: Mean=0, Std=1
- **Min-Max Scaling**: Range 0-1
- **Robust Scaling**: Uses median and IQR

## Feature Engineering

### Creating New Features:
1. **Mathematical Operations**: Sum, difference, ratios
2. **Date/Time Features**: Day of week, month, season
3. **Text Features**: Word count, sentiment scores
4. **Domain-Specific**: Customer lifetime value, click-through rates`,
      codeExamples: [
        {
          title: 'Complete Data Preprocessing Pipeline',
          language: 'python',
          code: `import pandas as pd
import numpy as np
from sklearn.preprocessing import StandardScaler, LabelEncoder
from sklearn.impute import SimpleImputer

# Load sample data
data = pd.DataFrame({
    'age': [25, 30, np.nan, 45, 35],
    'income': [50000, 60000, 55000, np.nan, 70000],
    'education': ['High School', 'Bachelor', 'Master', 'Bachelor', 'PhD'],
    'purchased': [1, 0, 1, 1, 0]
})

# Handle missing numerical data
numeric_imputer = SimpleImputer(strategy='median')
data[['age', 'income']] = numeric_imputer.fit_transform(data[['age', 'income']])

# Encode categorical data
label_encoder = LabelEncoder()
data['education_encoded'] = label_encoder.fit_transform(data['education'])

# Scale numerical features
scaler = StandardScaler()
data[['age_scaled', 'income_scaled']] = scaler.fit_transform(data[['age', 'income']])

print(data)`,
          explanation: 'This pipeline demonstrates the essential steps of data preprocessing: handling missing values, encoding categorical variables, and scaling numerical features.'
        }
      ],
      practice: {
        task: 'Clean and preprocess a dataset with missing values and categorical variables.',
        starter: `import pandas as pd
import numpy as np
from sklearn.preprocessing import StandardScaler, LabelEncoder
from sklearn.impute import SimpleImputer

# Your task: Clean this messy dataset
raw_data = pd.DataFrame({
    'customer_age': [25, np.nan, 35, 42, 28, 55],
    'annual_income': [45000, 62000, np.nan, 78000, 51000, 95000],
    'product_category': ['Electronics', 'Clothing', 'Electronics', 'Books', np.nan, 'Electronics'],
    'satisfaction_score': [4.2, 3.8, np.nan, 4.7, 3.9, 4.1]
})

# TODO: 
# 1. Handle missing values appropriately
# 2. Encode categorical variables
# 3. Scale numerical features
# 4. Create a new feature: income_per_age

# Your code here...`,
        hints: [
          'Use SimpleImputer for numerical missing values',
          'Handle categorical missing values before encoding',
          'Consider which scaling method is most appropriate',
          'New features can provide additional insights'
        ]
      }
    },
    {
      id: 'ml-supervised-basics',
      title: 'Supervised Learning Fundamentals',
      description: 'Master linear regression, logistic regression, and decision trees - the foundation of supervised learning.',
      duration: '2.5 hours',
      difficulty: 'beginner',
      objectives: [
        'Implement linear and logistic regression',
        'Build and interpret decision trees',
        'Understand when to use each algorithm',
        'Evaluate model performance'
      ],
      content: `# Supervised Learning Fundamentals

Supervised learning is the most common type of machine learning, where we learn from labeled examples.

## Linear Regression

### Concept:
- Predicts continuous numerical values
- Finds the best line through data points
- Minimizes prediction errors

### Use Cases:
- Price prediction
- Sales forecasting
- Temperature prediction

### Key Assumptions:
1. Linear relationship between features and target
2. Independence of observations
3. Constant variance of errors

## Logistic Regression

### Concept:
- Predicts probabilities for classification
- Uses logistic function (S-curve)
- Outputs between 0 and 1

### Use Cases:
- Email spam detection
- Medical diagnosis
- Marketing response prediction

### Advantages:
- Provides probability estimates
- No assumptions about feature distributions
- Less prone to overfitting

## Decision Trees

### How They Work:
1. Start with entire dataset
2. Find best feature to split on
3. Create branches for each split
4. Repeat until stopping criteria met

### Advantages:
- Easy to interpret and visualize
- Handles both numerical and categorical data
- No need for feature scaling
- Can capture non-linear patterns

### Disadvantages:
- Prone to overfitting
- Can be unstable (small data changes = different tree)
- Bias toward features with more levels

## Choosing the Right Algorithm

### Linear Regression When:
- Target is continuous
- Linear relationship exists
- Interpretability is important

### Logistic Regression When:
- Binary classification problem
- Need probability estimates
- Features are roughly linear

### Decision Trees When:
- Non-linear patterns exist
- Mixed data types
- Interpretability is crucial
- Feature interactions are important`,
      codeExamples: [
        {
          title: 'Comparing Supervised Learning Algorithms',
          language: 'python',
          code: `from sklearn.model_selection import train_test_split
from sklearn.linear_model import LinearRegression, LogisticRegression
from sklearn.tree import DecisionTreeClassifier, DecisionTreeRegressor
from sklearn.metrics import accuracy_score, mean_squared_error
import pandas as pd
import numpy as np

# Create sample datasets
# Regression dataset
np.random.seed(42)
X_reg = np.random.randn(1000, 2)
y_reg = 3*X_reg[:, 0] + 2*X_reg[:, 1] + np.random.randn(1000)*0.1

# Classification dataset
X_class = np.random.randn(1000, 2)
y_class = (X_class[:, 0] + X_class[:, 1] > 0).astype(int)

# Split data
X_reg_train, X_reg_test, y_reg_train, y_reg_test = train_test_split(X_reg, y_reg, test_size=0.2)
X_class_train, X_class_test, y_class_train, y_class_test = train_test_split(X_class, y_class, test_size=0.2)

# Linear Regression
lin_reg = LinearRegression()
lin_reg.fit(X_reg_train, y_reg_train)
reg_pred = lin_reg.predict(X_reg_test)
print(f"Linear Regression MSE: {mean_squared_error(y_reg_test, reg_pred):.4f}")

# Decision Tree Regression
tree_reg = DecisionTreeRegressor(max_depth=5)
tree_reg.fit(X_reg_train, y_reg_train)
tree_reg_pred = tree_reg.predict(X_reg_test)
print(f"Decision Tree Regression MSE: {mean_squared_error(y_reg_test, tree_reg_pred):.4f}")

# Logistic Regression
log_reg = LogisticRegression()
log_reg.fit(X_class_train, y_class_train)
log_pred = log_reg.predict(X_class_test)
print(f"Logistic Regression Accuracy: {accuracy_score(y_class_test, log_pred):.4f}")

# Decision Tree Classification
tree_class = DecisionTreeClassifier(max_depth=5)
tree_class.fit(X_class_train, y_class_train)
tree_pred = tree_class.predict(X_class_test)
print(f"Decision Tree Classification Accuracy: {accuracy_score(y_class_test, tree_pred):.4f}")`,
          explanation: 'This example demonstrates how to implement and compare different supervised learning algorithms for both regression and classification tasks.'
        }
      ]
    }
    // Additional modules would be added here following the same pattern
  ]
};
